/**
 * Przegląd portfolio.
 *
 * Widok jest ułożony w cztery poziomy i kolejność nie jest kwestią gustu —
 * to jest kolejność, w jakiej właściciel zadaje pytania:
 *
 *   1. Ile mamy pieniędzy?              → jedna liczba wiodąca
 *   2. Skąd się biorą klienci?          → lejek
 *   3. Dlaczego przychód się zmienił?   → most MRR i szereg czasowy
 *   4. Który produkt wymaga uwagi?      → tabela produktów
 *
 * Wszystko poza tym siedzi w szufladzie „Wszystkie wskaźniki". Poprzednia
 * wersja miała dwanaście kafelków o identycznej wadze wizualnej — ARPA obok
 * MRR, LTV/CAC obok liczby klientów. Kiedy wszystko jest równie ważne, nic
 * nie jest ważne i każde pytanie kosztuje tyle samo szukania.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { Chart } from "../components/Chart";
import { FunnelFlow, FunnelSetupNote, FunnelStockPanel } from "../components/Funnel";
import { RangePicker } from "../components/RangePicker";
import {
  Card,
  DataGaps,
  Drawer,
  Empty,
  ErrorState,
  FreshnessBar,
  Headline,
  Level,
  Loading,
  MetricText,
  StatusChip,
  Tile,
} from "../components/ui";
import { api } from "../lib/api";
import { useDemo } from "../lib/demo";
import { lineOption, mrrBridgeOption, palette } from "../lib/charts";
import { count, dateShort, LABELS, money, percent, ratio } from "../lib/format";
import type { Funnel, PortfolioOverview, ProductRow, SeriesPoint } from "../lib/types";

export function PortfolioPage() {
  const [range, setRange] = useState("30d");
  const { scoped, key: demoKey, includeDemo, setIncludeDemo } = useDemo();

  const overview = useQuery({
    queryKey: ["portfolio", "overview", range, demoKey],
    queryFn: () => api.get<PortfolioOverview>(scoped(`/portfolio/overview?range=${range}`)),
  });

  const funnel = useQuery({
    queryKey: ["portfolio", "funnel", range, demoKey],
    queryFn: () => api.get<Funnel>(scoped(`/portfolio/funnel?range=${range}`)),
  });

  const series = useQuery({
    queryKey: ["portfolio", "series", range, demoKey],
    queryFn: () =>
      api.get<{ reporting_currency: string; series: SeriesPoint[] }>(
        scoped(
          `/portfolio/series?range=${range}&fields=mrr_reporting_minor,customers_active,users_registered,organic_sessions,paid_sessions`,
        ),
      ),
  });

  const products = useQuery({
    queryKey: ["portfolio", "products", range, demoKey],
    queryFn: () =>
      api.get<{ period: { start: string; end: string }; rows: ProductRow[] }>(
        scoped(`/portfolio/products?range=${range}`),
      ),
  });

  const currency = overview.data?.organization.reporting_currency ?? "PLN";
  const asMoney = (value: number) => money(Math.round(value), currency, { compact: true });

  const charts = useMemo(() => {
    const rows = series.data?.series ?? [];
    if (!rows.length) return null;
    const p = palette();
    const days = rows.map((row) => dateShort(String(row.date)));
    const pick = (field: string) => rows.map((row) => Number(row[field] ?? 0));

    return {
      mrr: lineOption(
        days,
        [{ name: "MRR portfolio", values: pick("mrr_reporting_minor"), color: p.accent, area: true }],
        asMoney,
      ),
      customers: lineOption(
        days,
        [
          { name: "Klienci płacący", values: pick("customers_active"), color: p.series[1]!, area: true },
          { name: "Rejestracje", values: pick("users_registered"), color: p.series[2]! },
        ],
        (value) => count(Math.round(value)),
      ),
      traffic: lineOption(
        days,
        [
          { name: "Ruch organiczny", values: pick("organic_sessions"), color: p.series[5]! },
          { name: "Ruch płatny", values: pick("paid_sessions"), color: p.series[4]! },
        ],
        (value) => count(Math.round(value)),
      ),
    };
    // `asMoney` zależy tylko od waluty raportowej, która nie zmienia się w sesji.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series.data, currency]);

  const bridge = useMemo(() => {
    const current = overview.data?.current;
    if (!current) return null;
    const movements = current.mrr_movements;
    return mrrBridgeOption(
      {
        start: current.mrr_start_minor,
        new: movements.new_minor,
        expansion: movements.expansion_minor,
        reactivation: movements.reactivation_minor,
        contraction: movements.contraction_minor,
        churn: movements.churned_minor,
        end: current.mrr_minor,
      },
      asMoney,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overview.data, currency]);

  if (overview.isPending) return <Loading />;
  if (overview.isError) return <ErrorState error={overview.error} />;
  if (!overview.data) return <Empty title="Brak danych portfolio" />;

  const { current, previous, freshness } = overview.data;
  const derived = current.derived;
  const openAlerts = Object.values(overview.data.alerts).reduce((sum, value) => sum + value, 0);

  return (
    <>
      <header className="page-head">
        <div>
          <p className="page-head__eyebrow">Portfolio</p>
          <h1>Przegląd</h1>
          <p className="page-head__sub">
            {overview.data.products_count} produkty · waluta raportowa {currency} · doba biznesowa{" "}
            {overview.data.organization.reporting_timezone}
          </p>
        </div>
        <RangePicker value={range} onChange={setRange} />
      </header>

      <div className="stack">
        {includeDemo && products.data?.rows.some((row) => row.product.is_demo) && (
          <p className="demo-note">
            <strong>Widzisz dane demonstracyjne.</strong> W&nbsp;liczbach poniżej siedzą
            produkty wygenerowane na&nbsp;pokaz.{" "}
            <button type="button" onClick={() => setIncludeDemo(false)}>
              Pokaż tylko prawdziwe
            </button>
          </p>
        )}

        {overview.data.products_count === 0 && (
          <p className="demo-note demo-note--quiet">
            <strong>Nie ma tu jeszcze żadnego produktu.</strong>{" "}
            {includeDemo
              ? "Dodaj pierwszy na stronie wdrożenia — HQ zacznie liczyć, gdy CRM wyśle pierwsze zdarzenie."
              : "Dane demonstracyjne są odcięte, a prawdziwego produktu jeszcze nie podpięto."}{" "}
            <Link to="/wdrozenie">Przejdź do wdrożenia</Link>
            {!includeDemo && (
              <button type="button" onClick={() => setIncludeDemo(true)}>
                albo pokaż z powrotem demo
              </button>
            )}
          </p>
        )}

        <FreshnessBar items={freshness} />
        <DataGaps gaps={current.data_gaps} />

        {current.cost_warnings?.length > 0 && (
          <p className="gap-note" role="alert">
            {current.cost_warnings.join(" ")} <Link to="/koszty">Otwórz koszty</Link>
          </p>
        )}

        <Headline
          label={`MRR portfolio · ${currency}`}
          value={money(current.mrr_minor, currency)}
          current={current.mrr_minor}
          previous={previous.mrr_minor}
          meta={
            openAlerts > 0 ? (
              <Link to="/alerty" style={{ color: "var(--crit)" }}>
                {openAlerts} otwartych alertów
              </Link>
            ) : (
              <span>bez otwartych alertów</span>
            )
          }
          support={[
            {
              label: "ARR",
              value: money(current.arr_minor, currency, { compact: true }),
              foot: "MRR × 12",
            },
            {
              label: "Klienci płacący",
              value: count(current.customers),
              foot: `+${count(current.customers_new)} nowych w okresie`,
            },
            {
              label: "Marża miesięczna",
              value: money(current.gross_margin_minor, currency, { compact: true }),
              foot: "po kosztach i reklamie",
              title:
                "MRR minus koszty produktów, koszty ogólne pracowni i reklama, w skali miesiąca",
            },
          ]}
        />

        <Level
          title="Lejek"
          question="Skąd biorą się klienci i na którym kroku ich tracimy?"
        >
          {funnel.isPending ? (
            <Loading label="Składam lejek…" />
          ) : funnel.isError ? (
            <ErrorState error={funnel.error} />
          ) : funnel.data ? (
            <>
              <FunnelSetupNote setup={funnel.data.setup} />
              <div className="grid grid--2">
                <Card
                  title="Lejek okresu"
                  note="Długość paska to konwersja z poprzedniego etapu — przy udziale w ruchu ostatnie etapy byłyby niewidoczne"
                >
                  <FunnelFlow stages={funnel.data.flow} />
                </Card>
                <Card
                  title="Stan bazy"
                  note={`Ile kont żyje i na czym siedzą · stan na ${funnel.data.stock.as_of}`}
                >
                  <FunnelStockPanel stock={funnel.data.stock} currency={currency} />
                </Card>
              </div>
            </>
          ) : null}
        </Level>

        <Level title="Przychód" question="Skąd wzięła się zmiana MRR?">
          <div className="grid grid--2">
            <Card title="MRR w czasie" note={`Stan na koniec każdego dnia · ${currency}`}>
              <Chart option={charts?.mrr ?? null} ariaLabel="Wykres MRR portfolio w czasie" />
            </Card>
            <Card
              title="Most MRR"
              note="Suma słupków domyka różnicę między początkiem a końcem okresu"
            >
              <Chart option={bridge} ariaLabel="Most MRR: nowi, rozwój, powroty, obniżki, utraty" />
            </Card>
          </div>
        </Level>

        <Level
          title="Produkty"
          question="Który produkt wymaga uwagi?"
          action={
            products.data && (
              <span className="level__question mono">
                {products.data.period.start} — {products.data.period.end}
              </span>
            )
          }
        >
          <Card flush>
            {products.isPending ? (
              <Loading label="Liczę produkty…" />
            ) : products.data?.rows.length ? (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>Produkt</th>
                      <th>Status</th>
                      <th className="num">MRR</th>
                      <th className="num">Zmiana</th>
                      <th className="num">Klienci</th>
                      <th className="num">Churn</th>
                      <th className="num">Health</th>
                      <th className="num">Alerty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.data.rows.map((row) => {
                      const metrics = row.metrics;
                      const change =
                        row.previous.mrr_minor > 0
                          ? (metrics.mrr_minor - row.previous.mrr_minor) / row.previous.mrr_minor
                          : null;
                      return (
                        <tr key={row.product.slug}>
                          <td>
                            <Link to={`/produkt/${row.product.slug}`}>
                              <span
                                className="navlink__dot"
                                style={{
                                  color: row.product.accent_color,
                                  display: "inline-block",
                                  marginRight: "0.5rem",
                                }}
                                aria-hidden="true"
                              />
                              {row.product.name}
                            </Link>
                          </td>
                          <td>
                            <StatusChip
                              value={row.product.status}
                              dictionary={LABELS.productStatus}
                            />
                            {row.product.is_demo && (
                              <span className="tag-demo" title="Produkt demonstracyjny">
                                demo
                              </span>
                            )}
                          </td>
                          <td className="num">{money(metrics.mrr_minor, currency)}</td>
                          <td className="num">
                            {change === null ? (
                              <span className="dim">—</span>
                            ) : (
                              <span
                                className={
                                  change > 0.0005
                                    ? "delta delta--up"
                                    : change < -0.0005
                                      ? "delta delta--down"
                                      : "delta delta--flat"
                                }
                              >
                                {change > 0 ? "+" : ""}
                                {percent(change, 1)}
                              </span>
                            )}
                          </td>
                          <td className="num">{count(metrics.customers)}</td>
                          <td className="num">
                            <MetricText
                              metric={metrics.derived.customer_churn}
                              render={(value) => percent(value)}
                            />
                          </td>
                          <td className="num">
                            {row.health ? (
                              <span title={`pewność ${percent(row.health.confidence, 0)}`}>
                                {row.health.score.toFixed(0)} {row.health.grade}
                              </span>
                            ) : (
                              <span className="dim">—</span>
                            )}
                          </td>
                          <td className="num">
                            {row.open_alerts > 0 ? (
                              <Link to="/alerty" style={{ color: "var(--crit)" }}>
                                {row.open_alerts}
                              </Link>
                            ) : (
                              <span className="dim">0</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <Empty
                title="Nie ma jeszcze żadnego produktu"
                hint="Dodaj produkt przez API albo CLI."
              />
            )}
          </Card>
        </Level>

        <Level title="Szczegóły" question="Reszta wskaźników — potrzebna rzadziej niż raz dziennie">
          <Drawer summary="Wszystkie wskaźniki okresu">
            <div className="grid grid--tiles">
              <Tile
                label="Nowi klienci"
                value={count(current.customers_new)}
                current={current.customers_new}
                previous={previous.customers_new}
              />
              <Tile
                label="Churn klientów"
                value={
                  derived.customer_churn?.available && derived.customer_churn.value !== null
                    ? percent(derived.customer_churn.value)
                    : "—"
                }
                muted={!derived.customer_churn?.available}
                hint={derived.customer_churn?.reason ?? undefined}
                current={current.customers_churned}
                previous={previous.customers_churned}
                invertDelta
              />
              <Tile
                label="Churn przychodu"
                value={
                  derived.revenue_churn?.available && derived.revenue_churn.value !== null
                    ? percent(derived.revenue_churn.value)
                    : "—"
                }
                muted={!derived.revenue_churn?.available}
                hint={derived.revenue_churn?.reason ?? undefined}
              />
              <Tile
                label="Triale aktywne"
                value={count(current.trials_active)}
                current={current.trials_started}
                previous={previous.trials_started}
              />
              <Tile
                label="Konwersja triali"
                value={
                  derived.trial_conversion?.available && derived.trial_conversion.value !== null
                    ? percent(derived.trial_conversion.value, 0)
                    : "—"
                }
                muted={!derived.trial_conversion?.available}
                hint={derived.trial_conversion?.reason ?? undefined}
              />
              <Tile
                label="Wydatki reklamowe"
                value={money(current.ad_spend_minor, currency, { compact: true })}
                current={current.ad_spend_minor}
                previous={previous.ad_spend_minor}
                invertDelta
              />
              <Tile
                label="Koszty produktów"
                value={money(current.costs_minor, currency, { compact: true })}
                hint="Hosting, domeny, prowizje i podwykonawcy przypisani do produktów"
                current={current.costs_minor}
                previous={previous.costs_minor}
                invertDelta
              />
              <Tile
                label="Koszty ogólne"
                value={money(current.company_costs_minor, currency, { compact: true })}
                hint="Wydatki pracowni bez przypisania do produktu — obciążają marżę portfolio, nie marżę produktu"
                current={current.company_costs_minor}
                previous={previous.company_costs_minor}
                invertDelta
              />
              <Tile
                label="CAC"
                value={
                  derived.cac_minor?.available && derived.cac_minor.value !== null
                    ? money(derived.cac_minor.value, currency)
                    : "—"
                }
                muted={!derived.cac_minor?.available}
                hint={derived.cac_minor?.reason ?? undefined}
              />
              <Tile
                label="LTV / CAC"
                value={
                  derived.ltv_cac?.available && derived.ltv_cac.value !== null
                    ? ratio(derived.ltv_cac.value)
                    : "—"
                }
                muted={!derived.ltv_cac?.available}
                hint={derived.ltv_cac?.reason ?? undefined}
              />
            </div>

            <div className="grid grid--2" style={{ marginTop: "var(--space-5)" }}>
              <Card title="Klienci i rejestracje">
                <Chart
                  option={charts?.customers ?? null}
                  ariaLabel="Liczba płacących klientów i dziennych rejestracji"
                />
              </Card>
              <Card title="Ruch organiczny i płatny" note="Sesje dziennie">
                <Chart option={charts?.traffic ?? null} ariaLabel="Sesje organiczne i płatne" />
              </Card>
            </div>
          </Drawer>
        </Level>
      </div>
    </>
  );
}
